import { Platform } from '@angular/cdk/platform';
import {
    Directive,
    ElementRef,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    booleanAttribute,
    inject,
} from '@angular/core';
import { ScrollbarGeometry, ScrollbarPosition } from '@fuse/directives/scrollbar/scrollbar.types';
import { merge } from 'lodash-es';
import PerfectScrollbar from 'perfect-scrollbar';
import { Subject, debounceTime, fromEvent, takeUntil } from 'rxjs';

/**
 * Wrapper directive for the Perfect Scrollbar: https://github.com/mdbootstrap/perfect-scrollbar
 */
@Directive({
    selector: '[fuseScrollbar]',
    exportAs: 'fuseScrollbar',
    standalone: true,
})
export class FuseScrollbarDirective implements OnChanges, OnInit, OnDestroy {
    private readonly hostRef = inject(ElementRef);
    private readonly platform = inject(Platform);

    @Input({ transform: booleanAttribute }) fuseScrollbar = true;
    @Input() fuseScrollbarOptions: PerfectScrollbar.Options = {};

    private animation: number | null = null;
    private options: PerfectScrollbar.Options = {};
    private ps: PerfectScrollbar | null = null;
    private readonly unsubscribeAll: Subject<void> = new Subject<void>();

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    get elementRef(): ElementRef {
        return this.hostRef;
    }

    get scrollbar(): PerfectScrollbar | null {
        return this.ps;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    ngOnChanges(changes: SimpleChanges): void {
        // Enabled
        if ('fuseScrollbar' in changes) {
            if (this.fuseScrollbar) {
                this.initScrollbar();
            } else {
                this.destroyScrollbar();
            }
        }

        // Scrollbar options
        if ('fuseScrollbarOptions' in changes) {
            // Merge the options
            this.options = merge({}, this.options, changes.fuseScrollbarOptions.currentValue);

            // Return if not initialized
            if (!this.ps) {
                return;
            }

            // Destroy and re-init the PerfectScrollbar to update its options
            setTimeout(() => {
                this.destroyScrollbar();
                this.initScrollbar();
            });
        }
    }

    ngOnInit(): void {
        // Subscribe to window resize event
        fromEvent(window, 'resize')
            .pipe(takeUntil(this.unsubscribeAll), debounceTime(150))
            .subscribe(() => {
                // Update the PerfectScrollbar
                this.update();
            });
    }

    ngOnDestroy(): void {
        this.destroyScrollbar();

        // Unsubscribe from all subscriptions
        this.unsubscribeAll.next();
        this.unsubscribeAll.complete();
    }

    protected isEnabled(): boolean {
        return this.fuseScrollbar;
    }

    protected update(): void {
        // Return if not initialized
        if (!this.ps) {
            return;
        }

        // Update the PerfectScrollbar
        this.ps.update();
    }

    protected destroy(): void {
        this.destroyScrollbar();
    }

    protected geometry(prefix: string = 'scroll'): ScrollbarGeometry {
        return new ScrollbarGeometry(
            this.hostRef.nativeElement[prefix + 'Left'],
            this.hostRef.nativeElement[prefix + 'Top'],
            this.hostRef.nativeElement[prefix + 'Width'],
            this.hostRef.nativeElement[prefix + 'Height'],
        );
    }

    protected position(absolute: boolean = false): ScrollbarPosition {
        let scrollbarPosition;

        if (!absolute && this.ps) {
            scrollbarPosition = new ScrollbarPosition(this.ps.reach.x || 0, this.ps.reach.y || 0);
        } else {
            scrollbarPosition = new ScrollbarPosition(
                this.hostRef.nativeElement.scrollLeft,
                this.hostRef.nativeElement.scrollTop,
            );
        }

        return scrollbarPosition;
    }

    protected scrollTo(x: number, y?: number, speed?: number): void {
        if (y == null && speed == null) {
            this.animateScrolling('scrollTop', x, speed);
        } else {
            if (x != null) {
                this.animateScrolling('scrollLeft', x, speed);
            }

            if (y != null) {
                this.animateScrolling('scrollTop', y, speed);
            }
        }
    }

    protected scrollToX(x: number, speed?: number): void {
        this.animateScrolling('scrollLeft', x, speed);
    }

    protected scrollToY(y: number, speed?: number): void {
        this.animateScrolling('scrollTop', y, speed);
    }

    protected scrollToTop(offset: number = 0, speed?: number): void {
        this.animateScrolling('scrollTop', offset, speed);
    }

    protected scrollToBottom(offset: number = 0, speed?: number): void {
        const top = this.hostRef.nativeElement.scrollHeight - this.hostRef.nativeElement.clientHeight;
        this.animateScrolling('scrollTop', top - offset, speed);
    }

    protected scrollToLeft(offset: number = 0, speed?: number): void {
        this.animateScrolling('scrollLeft', offset, speed);
    }

    protected scrollToRight(offset: number = 0, speed?: number): void {
        const left = this.hostRef.nativeElement.scrollWidth - this.hostRef.nativeElement.clientWidth;
        this.animateScrolling('scrollLeft', left - offset, speed);
    }

    protected scrollToElement(qs: string, offset: number = 0, ignoreVisible: boolean = false, speed?: number): void {
        const element = this.hostRef.nativeElement.querySelector(qs);

        if (!element) {
            return;
        }

        const elementPos = element.getBoundingClientRect();
        const scrollerPos = this.hostRef.nativeElement.getBoundingClientRect();

        if (this.hostRef.nativeElement.classList.contains('ps--active-x')) {
            if (ignoreVisible && elementPos.right <= scrollerPos.right - Math.abs(offset)) {
                return;
            }

            const currentPos = this.hostRef.nativeElement['scrollLeft'];
            const position = elementPos.left - scrollerPos.left + currentPos;

            this.animateScrolling('scrollLeft', position + offset, speed);
        }

        if (this.hostRef.nativeElement.classList.contains('ps--active-y')) {
            if (ignoreVisible && elementPos.bottom <= scrollerPos.bottom - Math.abs(offset)) {
                return;
            }

            const currentPos = this.hostRef.nativeElement['scrollTop'];
            const position = elementPos.top - scrollerPos.top + currentPos;

            this.animateScrolling('scrollTop', position + offset, speed);
        }
    }

    protected animateScrolling(target: string, value: number, speed?: number): void {
        if (this.animation) {
            window.cancelAnimationFrame(this.animation);
            this.animation = null;
        }

        if (!speed || typeof window === 'undefined') {
            this.hostRef.nativeElement[target] = value;
        } else if (value !== this.hostRef.nativeElement[target]) {
            let newValue = 0;
            let scrollCount = 0;

            let oldTimestamp = performance.now();
            let oldValue = this.hostRef.nativeElement[target];

            const cosParameter = (oldValue - value) / 2;

            const step = (newTimestamp: number): void => {
                scrollCount += Math.PI / (speed / (newTimestamp - oldTimestamp));
                newValue = Math.round(value + cosParameter + cosParameter * Math.cos(scrollCount));

                // Only continue animation if scroll position has not changed
                if (this.hostRef.nativeElement[target] === oldValue) {
                    if (scrollCount >= Math.PI) {
                        this.animateScrolling(target, value, 0);
                    } else {
                        this.hostRef.nativeElement[target] = newValue;

                        // On a zoomed out page the resulting offset may differ
                        oldValue = this.hostRef.nativeElement[target];
                        oldTimestamp = newTimestamp;

                        this.animation = window.requestAnimationFrame(step);
                    }
                }
            };

            window.requestAnimationFrame(step);
        }
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    private initScrollbar(): void {
        // Return if already initialized
        if (this.ps) {
            return;
        }

        // Return if on mobile or not on browser
        if (this.platform.ANDROID || this.platform.IOS || !this.platform.isBrowser) {
            this.fuseScrollbar = false;
            return;
        }

        // Initialize the PerfectScrollbar
        this.ps = new PerfectScrollbar(this.hostRef.nativeElement, {
            ...this.options,
        });
    }

    private destroyScrollbar(): void {
        // Return if not initialized
        if (!this.ps) {
            return;
        }

        // Destroy the PerfectScrollbar
        this.ps.destroy();

        // Clean up
        this.ps = null;
    }
}
